import { useEffect, useRef, useState } from 'react';
import { quotePdfBlobUrl } from '../utils/quotePdf';

/**
 * Rebuilds the quote PDF whenever the quote changes and hands back an object
 * URL for an <iframe>.
 *
 * Three things this has to get right:
 *   · Debounce — a keystroke must not trigger a full jsPDF render.
 *   · Staleness — an in-flight build for an older quote must not overwrite a
 *     newer one, so each run carries a token that is checked before commit.
 *   · Leaks — every object URL we mint has to be revoked, including the last
 *     one on unmount, or the blob stays resident for the tab's lifetime.
 *
 * @param {object} quote
 * @param {boolean} enabled  false while the pane is hidden, so we render nothing
 * @param {number} delay     debounce window in ms
 */
export function useLiveQuotePdf(quote, enabled = true, delay = 450) {
  const [url, setUrl] = useState('');
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');

  const runIdRef = useRef(0);
  const urlRef = useRef('');

  const replaceUrl = (next) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = next;
    setUrl(next);
  };

  useEffect(() => {
    if (!enabled) return undefined;

    // Nothing to draw yet — clear rather than render an empty document.
    if (!quote?.lines?.length) {
      replaceUrl('');
      setBuilding(false);
      setError('');
      return undefined;
    }

    const runId = ++runIdRef.current;
    setBuilding(true);

    const timer = setTimeout(async () => {
      try {
        const next = await quotePdfBlobUrl(quote);
        if (runId !== runIdRef.current) {
          // A newer edit landed while we were rendering — drop this result.
          URL.revokeObjectURL(next);
          return;
        }
        replaceUrl(next);
        setError('');
      } catch (e) {
        if (runId === runIdRef.current) setError(e?.message || 'Preview failed');
      } finally {
        if (runId === runIdRef.current) setBuilding(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [quote, enabled, delay]);

  // Invalidate any in-flight build and release the last URL on unmount
  useEffect(() => () => {
    runIdRef.current++;
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  return { url, building, error };
}

export default useLiveQuotePdf;
